import Button from "@/components/common/Button";
import ToggleSwitch from "@/components/common/ToggleSwitch";
import Tooltip from "@/components/common/Tooltip";
import MultiAssetCheck from "@/pages/assets/_components/multiAssetCheck";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import dayjs from "dayjs";
import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import API from "@/api";
import Layout from "@/components/common/Layout";
import Box from "@/components/common/Box";


const AssetFaults = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const queryClient = useQueryClient();


  const [groupSelect, setGroupSelect] = useState([]);
  const [showMultiCheck, setShowMultiCheck] = useState(false);

  const faultyAssetsQuery = useQuery({
    queryKey: ["assets", "unresolved_faults"],
    queryFn: () => API.assets.reports.getUnresolvedFaultyAssets(),
    staleTime: 2000,
    refetchOnWindowFocus: true,
  });

  const combinedSearchObj = useMemo(() => {
    let result = {
      archived: false,
      query: "",
      tag: ""
    };

    if(search.get("archived") !== null){
      result.archived = search.get("archived");
    }
    if(search.get("query") !== null){
      result.query = search.get("query");
    }
    if(search.get("tag") !== null){
      result.tag = search.get("tag");
    }

    return result;
  }, [search])

  const setFilterTag = tag => {
    setSearch({
      ...combinedSearchObj,
      tag
    })
  }
  const setTextSearch = (e) => {
    setSearch({
      ...combinedSearchObj,
      query: e.target.value
    })
  }
  const setShowArchived = (val) => {
    setSearch({
      ...combinedSearchObj,
      archived: val ? "true" : "false",
    })
  }

  const showArchived = useMemo(() => {
    return combinedSearchObj.archived === "true";
  }, [combinedSearchObj])
  const tags = useMemo(() => {
    if(!faultyAssetsQuery.isSuccess) return [];

    return faultyAssetsQuery.data.reduce((curr, item) => {
      item.tags.forEach(tag => {
        if(curr.indexOf(tag) === -1) curr.push(tag);
      })
      return curr;
    }, []).sort((a, b) => {
      if(a > b) return 1;
      if(a < b) return -1;
      return 0;
    })
  }, [faultyAssetsQuery.isSuccess, faultyAssetsQuery.data])
  const filteredAssets = useMemo(() => {
    if(!faultyAssetsQuery.isSuccess) return [];

    return faultyAssetsQuery.data.filter(item => {
      if(item.archived !== showArchived) return false;

      if(combinedSearchObj.query.length > 0){
        const searchQuery = search.get("query");
        const isFround = (item.name || "").toLowerCase().search(searchQuery.toLowerCase()) !== -1 ||
        (item.serialNum || "").toLowerCase().search(searchQuery.toLowerCase()) !== -1;

        if(!isFround) return false;
      }

      if(combinedSearchObj.tag.length > 0){
        let hasTag = item.tags.filter((tag) => {
          return tag === combinedSearchObj.tag;
        });
        if(hasTag.length === 0) return false
      }
      return true;
    }).sort((a, b) => {
      if(a.name > b.name) return 1;
      if(a.name < b.name) return -1;
      return 0;
    })
  }, [combinedSearchObj, showArchived, faultyAssetsQuery.isSuccess, faultyAssetsQuery.data])
  const groupSelectedObjs = useMemo(() => {
    return groupSelect.map(a => {
      const found = filteredAssets.filter(b => b._id === a)
      if(found[0] !== undefined){
        return found[0];
      }
      return false;
    }).filter(a => a !== false)
  }, [groupSelect, filteredAssets]);

  const addToGroup = (id) => {
    let list = [];
    if (groupSelect.includes(id)) {
      let current = groupSelect.filter((item) => {
        return item !== id;
      });
      setGroupSelect(current);
    } else if (id) {
      list.push(id);
      setGroupSelect(groupSelect.concat(list));
    }
  };

  return (
    <Layout full>
      {showMultiCheck && <MultiAssetCheck assets={groupSelectedObjs} onClose={() => {
        setShowMultiCheck(false);
        queryClient.invalidateQueries(["assets", "unresolved_faults"]);
      }} />}

      <Box title={"Assets with Faults"}>
        <div className="grid sm:grid-cols-3">
          <div className="col-span-1 space-y-2 p-2">
            <input
              autoComplete="off"
              name="filter"
              placeholder="asset/serial number"
              onChange={setTextSearch}
              value={combinedSearchObj.query}
              className={`inputClass`}
            />
            <div className="flex items-center justify-between">
              <div className="px-2">Selected: {groupSelect.length}</div>
              <div className="p-2">
                <span
                  onClick={() => setShowArchived(true)}
                  className={`${showArchived && "bg-blue-900 text-white"} m-1 cursor-pointer  rounded-lg p-2 hover:bg-blue-800 dark:text-white`}
                >
                  Archived
                </span>
                <span
                  onClick={() => setShowArchived(false)}
                  className={`${!showArchived && "bg-blue-900 text-white"} m-1 cursor-pointer rounded-lg p-2 hover:bg-blue-800 dark:text-white`}
                >
                  Active
                </span>
              </div>
            </div>
          </div>
          <div className="col-span-2 ">
            <div className="col-span-2 flex flex-wrap gap-1 p-2">
              {tags.map((t, ti) => {
                return (
                  <span
                    onClick={() => {
                      if (t === combinedSearchObj.tag) {
                        setFilterTag("");
                      } else {
                        setFilterTag(t);
                      }
                    }}
                    key={ti}
                    className={`${
                      t === combinedSearchObj.tag
                        ? " bg-green-800 text-white hover:bg-green-800"
                        : " bg-zinc-300 dark:bg-gray-700"
                    }  cursor-pointer rounded-lg p-2 py-1.5 text-sm hover:bg-gray-600 dark:text-white`}
                  >{t}</span>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex gap-4 border-t border-zinc-300 p-2 dark:border-zinc-700">
          <Button
            colour={"text-sm bg-zinc-300 dark:bg-zinc-500 dark:border-zinc-400"}
            text="Add Check"
            onClick={() => setShowMultiCheck(true)}
            disabled={groupSelect.length <= 0}
          />
        </div>

        <table className="tableClass">
          <thead>
          <tr className="tableHeadRowClass">
            <th className="tableHeadCellClass"></th>
            <th className="tableHeadCellClass">Asset</th>
            <th className="tableHeadCellClass">Asset Number</th>
            <th className="tableHeadCellClass">Status</th>
            <th className="tableHeadCellClass">Last Check</th>
            <th className="tableHeadCellClass">Tags</th>
          </tr>
          </thead>
          <tbody className="tableBodyClass">
          {faultyAssetsQuery.isLoading ? (
            <tr>
              <td colSpan="86" className="h-12 p-4 text-2xl">
                Loading...
              </td>
            </tr>
          ) : (
            filteredAssets.map((a) => {
              const onViewClick = () => {
                navigate(`/asset/view/${a._id}`);
              };

              let status = "";
              switch (true) {
                case a.status === "Non-Operational" || a.status === "Out for Repair":
                  status = "bg-red-900 ";
                  break;
                case a.status === "Faulty":
                  status = "bg-yellow-900 ";
                  break;
                default:
                  status = "bg-green-900 ";
                  break;
              }
              let lastCheck = "";
              if (a.lastCheck.status) {
                switch (true) {
                  case a.lastCheck.status === "scheduled":
                    lastCheck = " ";
                    break;
                  case a.lastCheck.status === "OK":
                    lastCheck = "bg-green-900 text-white";
                    break;
                  default:
                    lastCheck = "bg-red-900 text-white";
                    break;
                }
              }
              let expires = "";
              switch (true) {
                case dayjs(a.date_expires).isBefore(dayjs()):
                  expires = " bg-red-900 bg-opacity-50 ";
                  break;
                case dayjs(a.date_expires).isAfter(dayjs()) &&
                dayjs(a.date_expires).isBefore(dayjs().add(3, "month")):
                  expires = " bg-yellow-900 bg-opacity-50 ";
                  break;
                case dayjs(a.date_expires).isAfter(dayjs()) &&
                dayjs(a.date_expires).isAfter(dayjs().add(3, "month")):
                  expires = " bg-green-900 bg-opacity-50 ";
                  break;

                default:
                  expires = " ";
                  break;
              }

              return (
                <tr
                  key={a._id}
                  className={`tableBodyRowClass cursor-pointer text-sm`}
                >
                  <td className="w-6 p-2">
                    <ToggleSwitch
                      isChecked={groupSelect.includes(a._id)}
                      onChange={() => addToGroup(a._id)}
                    />
                  </td>
                  <td className="p-2" onClick={onViewClick}>
                    {a.name}
                  </td>
                  <td className="w-1/8 p-2" onClick={onViewClick}>
                    {a.assetNum}
                  </td>
                  <td
                    className={`p-2 ${status} w-1/8 bg-opacity-50`}
                    onClick={onViewClick}
                  >
                    {a.status}
                  </td>
                  <td
                    className={`p-2 nonOp`}
                    onClick={onViewClick}
                  >
                    {a.lastCheck.date_checked ? dayjs(a.lastCheck.date_checked).format("DD-MM-YYYY") : "--"} by: {a.lastCheck.checked_By}
                    <br />
                    {a.lastCheck.note}
                  </td>
                  <td className="p-2" onClick={onViewClick}>
                    {a.tags?.map((t, i) => {
                      return (
                        <span key={i} className="m-1 rounded-lg border border-gray-700 p-2 dark:bg-gray-800">{t}</span>
                      );
                    })}
                  </td>
                </tr>
              );
            })
          )}
          </tbody>
        </table>
      </Box>
    </Layout>
  );
}
export default AssetFaults;